/**
 * @quotech/auth - Auth Hook
 * 
 * Convenience hook for accessing auth state and actions.
 */

import { useCallback } from 'react'
import { useAuthStore } from './authStore'
import { MASTER_LOGIN_URL } from './cookieAuth'
import type { User, TokenPair } from './types'

export interface UseAuthReturn {
  // State
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  organisationId: string | null
  organisationName: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Derived
  userName: string
  userEmail: string
  userInitials: string
  isAdmin: boolean

  // Actions
  login: (tokens: TokenPair, user: User) => void
  logout: () => void
  checkAuth: () => Promise<boolean>
  updateUser: (user: Partial<User>) => void
}

export function useAuth(): UseAuthReturn {
  const store = useAuthStore()

  // Derived values
  const userName = store.user?.name || store.user?.full_name || store.user?.email || 'User'
  const userEmail = store.user?.email || ''
  const userInitials = userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
  const isAdmin = store.user?.role === 'admin' || store.user?.permissions?.includes('admin') || false

  // Actions
  const login = useCallback((tokens: TokenPair, user: User) => {
    store.setAuth(tokens, user)
  }, [store])

  const logout = useCallback(() => {
    store.clearAuth()
    // Redirect to dashboard (environment-aware)
    window.location.href = MASTER_LOGIN_URL
  }, [store])

  return {
    // State
    user: store.user,
    accessToken: store.accessToken,
    refreshToken: store.refreshToken,
    organisationId: store.organisationId,
    organisationName: store.organisationName,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,

    // Derived
    userName,
    userEmail,
    userInitials,
    isAdmin,

    // Actions
    login,
    logout,
    checkAuth: store.checkAuth,
    updateUser: store.updateUser,
  }
}

export default useAuth

